import React, { useState } from "react";

// Utility functions
function buf2hex(buffer) {
    return Array.prototype.map.call(new Uint8Array(buffer), (x) => ("00" + x.toString(16)).slice(-2)).join("");
}

function hex2buf(hex) {
    if (!hex) return new Uint8Array();
    const pairs = hex.match(/.{1,2}/g) || [];
    return new Uint8Array(pairs.map((b) => parseInt(b, 16)));
}

async function sha256Web(bytes) {
    const data = bytes instanceof Uint8Array ? bytes : new TextEncoder().encode(bytes);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return buf2hex(digest);
}

export default function SafeMnemonic() {
    const [mnemonic, setMnemonic] = useState("");
    const [password, setPassword] = useState("");
    const [status, setStatus] = useState("");
    const [resultJson, setResultJson] = useState(null);
    const [decrypted, setDecrypted] = useState("");
    const [error, setError] = useState("");
    const [hashInput, setHashInput] = useState("");
    const [uploadRequired, setUploadRequired] = useState(false);

    // ------------------ ENCRYPT ------------------
    async function handleEncrypt() {
        try {
            setError(""); setStatus("Working..."); setResultJson(null); setDecrypted("");
            if (!mnemonic.trim()) throw new Error("Enter your mnemonic (12/24/48 words).");
            if (!password.trim() || password.length < 8) throw new Error("Password too short (>= 8 chars).");

            const salt = crypto.getRandomValues(new Uint8Array(32));
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const pwBytes = new TextEncoder().encode(password);

            const aesKeyBytes = await crypto.subtle.importKey(
                "raw",
                pwBytes,
                { name: "PBKDF2" },
                false,
                ["deriveBits"]
            ).then(async key =>
                new Uint8Array(await crypto.subtle.deriveBits(
                    { name: "PBKDF2", salt, iterations: 250000, hash: "SHA-256" },
                    key,
                    256
                ))
            );

            const cryptoKey = await crypto.subtle.importKey(
                "raw",
                aesKeyBytes.buffer,
                { name: "AES-GCM", length: 256 },
                false,
                ["encrypt", "decrypt"]
            );

            const enc = new TextEncoder().encode(mnemonic.trim());
            const ciphertextBuffer = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, enc);

            const ciphertextHex = buf2hex(ciphertextBuffer);
            const saltHex = buf2hex(salt.buffer);
            const ivHex = buf2hex(iv.buffer);
            const finalHash = await sha256Web(new TextEncoder().encode(`${mnemonic.trim()}:${saltHex}`));

            const packageJson = {
                version: 1,
                kdf: { pbkdf2: { iterations: 250000, hash: "SHA-256" } },
                salt: saltHex,
                iv: ivHex,
                ciphertext: ciphertextHex,
                hash: finalHash,
                createdAt: new Date().toISOString(),
            };

            localStorage.setItem(finalHash, JSON.stringify(packageJson));

            setResultJson(packageJson);
            setStatus("✅ Encryption done. Keep your password safe!");
        } catch (e) {
            console.error(e);
            setError(e.message || "Encryption failed");
            setStatus("");
        }
    }

    // ------------------ DOWNLOAD JSON ------------------
    function downloadJson() {
        if (!resultJson) return;
        const blob = new Blob([JSON.stringify(resultJson, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mnemonic-backup.json";
        a.click();
        URL.revokeObjectURL(url);
    }

    // ------------------ HANDLE UPLOAD JSON ------------------
    function handleUploadJsonFile(file) {
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const json = JSON.parse(event.target.result);
                if (!json.hash || json.hash !== hashInput.trim()) {
                    throw new Error("JSON does not match the requested hash.");
                }
                localStorage.setItem(json.hash, JSON.stringify(json));
                setUploadRequired(false);
                setStatus("✅ JSON uploaded. Attempting decryption...");
                await handleDecrypt(); // נסה לפענח שוב
            } catch (err) {
                console.error(err);
                setError("❌ Failed to parse JSON or hash mismatch.");
                setStatus("");
            }
        };
        reader.readAsText(file);
    }

    // ------------------ DECRYPT ------------------
    async function handleDecrypt() {
        try {
            setError(""); setDecrypted(""); setStatus("Decrypting...");
            if (!hashInput.trim()) throw new Error("Enter the hash for decryption.");
            if (!password.trim()) throw new Error("Enter your password.");

            let storedJson = localStorage.getItem(hashInput.trim());

            if (!storedJson) {
                setUploadRequired(true); // מבקש מהמשתמש להעלות JSON
                setStatus("⚠️ Hash not found. Please upload JSON backup.");
                return;
            }

            const data = JSON.parse(storedJson);
            const { salt, iv, ciphertext } = data;

            const saltBuf = hex2buf(salt);
            const ivBuf = hex2buf(iv);
            const ctBuf = hex2buf(ciphertext);
            const pwBytes = new TextEncoder().encode(password);

            const aesKeyBytes = await crypto.subtle.importKey(
                "raw",
                pwBytes,
                { name: "PBKDF2" },
                false,
                ["deriveBits"]
            ).then(async key =>
                new Uint8Array(await crypto.subtle.deriveBits(
                    { name: "PBKDF2", salt: saltBuf, iterations: 250000, hash: "SHA-256" },
                    key,
                    256
                ))
            );

            const cryptoKey = await crypto.subtle.importKey(
                "raw",
                aesKeyBytes.buffer,
                { name: "AES-GCM", length: 256 },
                false,
                ["decrypt"]
            );

            const plainBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv: ivBuf }, cryptoKey, ctBuf);
            const plain = new TextDecoder().decode(plainBuf);

            setDecrypted(plain);
            setStatus("✅ Decryption successful.");
        } catch (e) {
            console.error(e);
            setError(e.message || "❌ Decryption failed (wrong password or corrupted data).");
            setStatus("");
        }
    }

    // ------------------ RENDER ------------------
    return (
        <div className="qr-scanner-container">
            <a
                href="https://dr-brokers.com"
                style={{
                    display: 'block',
                    marginBottom: '20px',
                    color: '#007bff',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '16px'
                }}
            >
                Return to Home
            </a>
            <h1>Ultra-Secure Mnemonic Converter</h1>
            {/* ENCRYPT SECTION */}
            <div className="upload-section">
                <h3>Encrypt Mnemonic</h3>
                <p style={{ color: "#ffd700" }}>
                    ⚠️ Important: After encryption, <strong>save the JSON file</strong> and <strong>copy your hash</strong> securely.
                    You'll need both for decryption. Do NOT share your password!
                </p>
                <textarea
                    placeholder="Enter 12 / 24 / 48 words mnemonic"
                    value={mnemonic}
                    onChange={(e) => setMnemonic(e.target.value)}
                    rows={4}
                    style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "12px",
                        border: "1px solid #805A40",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        color: "#fff",
                        resize: "none"
                    }}
                />
                <input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "12px",
                        marginTop: "12px",
                        borderRadius: "12px",
                        border: "1px solid #805A40",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        color: "#fff"
                    }}
                />
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
                    <button className="btn-custom" onClick={handleEncrypt}>Encrypt</button>
                    <button className="btn-custom" onClick={downloadJson} disabled={!resultJson}>Download JSON</button>
                </div>

                {resultJson && (
                    <div style={{ marginTop: "20px", textAlign: "left", backgroundColor: "rgba(0,0,0,0.3)", padding: "15px", borderRadius: "12px", border: "1px solid #805A40" }}>
                        <h4>Encrypted Backup (JSON)</h4>
                        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "12px", color: "#fff" }}>{JSON.stringify(resultJson, null, 2)}</pre>
                        <p style={{ wordBreak: "break-word", color: "#00ff00" }}><strong>Hash:</strong> {resultJson.hash}</p>
                    </div>
                )}

                {/* DECRYPT SECTION */}
                <h3 style={{ marginTop: "40px" }}>Decrypt Mnemonic</h3>
                <p style={{ color: "#ffd700" }}>
                    ⚠️ Enter your hash and password. If the hash is missing, upload the JSON backup you saved earlier.
                </p>
                <input
                    type="text"
                    placeholder="Enter hash"
                    value={hashInput}
                    onChange={(e) => setHashInput(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "12px",
                        marginTop: "12px",
                        borderRadius: "12px",
                        border: "1px solid #805A40",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        color: "#fff"
                    }}
                />
                <input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "12px",
                        marginTop: "12px",
                        borderRadius: "12px",
                        border: "1px solid #805A40",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        color: "#fff"
                    }}
                />
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
                    <button className="btn-custom" onClick={handleDecrypt}>Decrypt</button>
                </div>

                {/* FILE UPLOAD IF REQUIRED */}
                {uploadRequired && (
                    <div style={{ marginTop: "20px", textAlign: "center", backgroundColor: "rgba(0,0,0,0.2)", padding: "15px", borderRadius: "12px", border: "1px solid #805A40" }}>
                        <p style={{ color: "#ffd700" }}>⚠️ Hash not found. Please upload JSON backup:</p>
                        <input
                            type="file"
                            accept=".json"
                            onChange={(e) => handleUploadJsonFile(e.target.files[0])}
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "12px",
                                border: "1px solid #805A40",
                                backgroundColor: "rgba(0,0,0,0.3)",
                                color: "#fff"
                            }}
                        />
                    </div>
                )}

                {status && <p style={{ color: "#00ff00", marginTop: "12px" }}>{status}</p>}
                {error && <p style={{ color: "#ff4d4d", marginTop: "12px" }}>{error}</p>}
                {decrypted && (
                    <div style={{ marginTop: "16px", textAlign: "left", backgroundColor: "rgba(128,90,64,0.3)", padding: "12px", borderRadius: "12px", color: "#fff" }}>
                        <h4>Decrypted mnemonic</h4>
                        <div style={{ wordBreak: "break-word" }}>{decrypted}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
